import React, { useState, useMemo } from 'react';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { TicketType, TicketSeverity } from '../../../types/ticket';
import { TICKET_TYPE_LABELS, TICKET_SEVERITY_LABELS } from '../constants/ticketLabels';
import { classNames } from 'primereact/utils';
import './TicketFilters.scss';

interface TicketFiltersProps {
  title: string;
  type: TicketType[];
  severity: TicketSeverity[];
  onTitleChange: (title: string) => void;
  onTypeChange: (type: TicketType[]) => void;
  onSeverityChange: (severity: TicketSeverity[]) => void;
  onClearFilters: () => void;
}

const TYPE_OPTIONS: { label: string; value: TicketType }[] = [
  { label: TICKET_TYPE_LABELS.bug, value: 'bug' },
  { label: TICKET_TYPE_LABELS.feature, value: 'feature' },
  { label: TICKET_TYPE_LABELS.modification, value: 'modification' },
];

const SEVERITY_OPTIONS: { label: string; value: TicketSeverity }[] = [
  { label: TICKET_SEVERITY_LABELS.low, value: 'low' },
  { label: TICKET_SEVERITY_LABELS.medium, value: 'medium' },
  { label: TICKET_SEVERITY_LABELS.high, value: 'high' },
  { label: TICKET_SEVERITY_LABELS.critical, value: 'critical' },
];

export const TicketFilters = React.memo(({
  title,
  type,
  severity,
  onTitleChange,
  onTypeChange,
  onSeverityChange,
  onClearFilters,
}: TicketFiltersProps) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getActiveFiltersCount = useMemo(() => {
    let count = 0;
    if (title) count++;
    if (type.length > 0) count++;
    if (severity.length > 0) count++;
    return count;
  }, [title, type.length, severity.length]);

  const hasActiveFilters = useMemo(
    () => Boolean(title || type.length > 0 || severity.length > 0),
    [title, type.length, severity.length],
  );

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <div className='ticket-filters'>
      <div className='filters-header' onClick={toggleExpanded}>
        <div className='filters-header-content'>
          <i className={classNames('pi', isExpanded ? PrimeIcons.CHEVRON_DOWN : PrimeIcons.CHEVRON_RIGHT)}></i>
          <h5 className='filters-title'>Filtros</h5>
          {hasActiveFilters && <span className='active-filters-badge'>{getActiveFiltersCount}</span>}
        </div>
        <Button
          icon={PrimeIcons.CHEVRON_DOWN}
          className={classNames('toggle-button', { 'expanded': isExpanded })}
          text
          rounded
          onClick={(e) => {
            e.stopPropagation();
            toggleExpanded();
          }}
          aria-label={isExpanded ? 'Ocultar filtros' : 'Mostrar filtros'}
        />
      </div>
      {isExpanded && (
        <div className='filters-content'>
          <div className='filters-row'>
            <div className='filter-field'>
              <label htmlFor='filter-title'>Título</label>
              <InputText
                id='filter-title'
                value={title}
                onChange={(e) => onTitleChange(e.target.value)}
                placeholder='Buscar por título...'
                className='filter-input'
              />
            </div>

            <div className='filter-field'>
              <label htmlFor='filter-type'>Tipo</label>
              <MultiSelect
                id='filter-type'
                value={type}
                options={TYPE_OPTIONS}
                onChange={(e) => onTypeChange(e.value)}
                optionLabel='label'
                optionValue='value'
                placeholder='Seleccionar tipos'
                className='filter-multiselect'
                showSelectAll
                appendTo={document.body}
                panelStyle={{ minWidth: '200px', maxHeight: '300px', zIndex: 9999 }}
                autoOptionFocus={false}
                focusOnHover={false}
                selectOnFocus={false}
                selectedItemsLabel='{0} tipos seleccionados'
              />
            </div>

            <div className='filter-field'>
              <label htmlFor='filter-severity'>Severidad</label>
              <MultiSelect
                id='filter-severity'
                value={severity}
                options={SEVERITY_OPTIONS}
                onChange={(e) => onSeverityChange(e.value)}
                optionLabel='label'
                optionValue='value'
                placeholder='Seleccionar severidades'
                className='filter-multiselect'
                showSelectAll
                appendTo={document.body}
                panelStyle={{ minWidth: '200px', maxHeight: '300px', zIndex: 9999 }}
                autoOptionFocus={false}
                focusOnHover={false}
                selectOnFocus={false}
                selectedItemsLabel='{0} severidades seleccionadas'
              />
            </div>

            {hasActiveFilters && (
              <div className='filter-actions'>
                <Button
                  label='Limpiar filtros'
                  icon={PrimeIcons.TIMES}
                  onClick={onClearFilters}
                  className='clear-filters-button'
                  text
                  severity='secondary'
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});
